import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Check,
  Sparkles,
  Lock,
  Building2,
  PoundSterling,
  Users,
  Headphones,
  Receipt,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { BENEFITS } from "@/constants/testIds";

const cascadePlusIs = [
  "The only truly independent purchasing support service in the market",
  "Managed by individuals with pharma industry experience",
  "We don't own pharmacies \u2014 your interests are our only interests",
  "Unbiased to any wholesaler \u2014 your suppliers, your rules",
];

const youGet = [
  { icon: PoundSterling, title: "No joining fee", body: "Cascade Plus is free of charge - forever. No setup, no subscription." },
  { icon: Users, title: "You are in charge", body: "Your rules, your choices, your control - always." },
  { icon: Sparkles, title: "Hassle-free ordering", body: "One-click transmission directly from your PMR system." },
  { icon: Lock, title: "Works with all PMRs", body: "Plug into any UK PMR and use your customers' choice of suppliers." },
  { icon: TrendingUp, title: "Boost drug profitability", body: "Direct products to the right supplier and watch margin grow." },
  { icon: Receipt, title: "Cheapest commercial offering", body: "Always the best deal across wholesalers and short-liners." },
  { icon: Building2, title: "No more OOS chasing", body: "We handle out-of-stocks so your team can focus on patients." },
  { icon: Headphones, title: "Live human support 9-6", body: "Real UK-based pharma experts on the phone every weekday." },
];

export default function Benefits() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-emerald" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-12 gap-8 items-end"
          >
            <div className="lg:col-span-12">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">Benefits</div>
              <h1 data-testid={BENEFITS.pageTitle} className="text-[4.1rem] font-bold text-[#0A2540] tracking-tight leading-[1.04] mb-6">
                Control, margin, and less order chasing.
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
                The service is designed around the pharmacy team's real day: keep preferred suppliers, reduce manual work, and make every purchasing decision easier to explain.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TWO COLUMNS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl border border-slate-200 p-10 bg-white"
            >
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">What is Cascade Plus?</div>
              <h2 className="text-3xl font-bold text-[#0A2540] mb-6">Independent, by design.</h2>
              <ul className="space-y-4">
                {cascadePlusIs.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-700">
                    <Check className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-3xl bg-[#0A2540] p-10 text-white relative overflow-hidden"
            >
              <div className="absolute -top-20 -right-20 h-60 w-60 bg-emerald-500/20 blur-3xl rounded-full" />
              <div className="relative">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">In numbers</div>
                <h2 className="text-3xl font-bold mb-8">A free service that saves you thousands.</h2>
                <div className="space-y-6">
                  <div>
                    <div className="text-5xl font-bold mb-1">&pound;1,000<span className="text-emerald-400 text-2xl">/mo</span></div>
                    <div className="text-slate-400 text-sm">Average monthly saving</div>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div>
                    <div className="text-5xl font-bold mb-1">&pound;12,000<span className="text-emerald-400 text-2xl">/yr</span></div>
                    <div className="text-slate-400 text-sm">Average yearly saving</div>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div>
                    <div className="text-5xl font-bold mb-1">113k<span className="text-emerald-400 text-2xl">+</span></div>
                    <div className="text-slate-400 text-sm">Orders processed</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BENEFITS GRID */}
      <section className="py-20 bg-slate-50/60 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">What that means for you</div>
            <h2 className="text-4xl font-bold text-[#0A2540]">Eight benefits, zero compromises.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {youGet.map((b, i) => (
              <motion.div
                key={b.title}
                data-testid={BENEFITS.benefitItem(i + 1)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-300 hover:shadow-md transition-all"
              >
                <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                  <b.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-bold text-[#0A2540] mb-2">{b.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0A2540] mb-4">Sound like the procurement partner you've been waiting for?</h2>
          <p className="text-lg text-slate-600 mb-8">Get a free quote based on your current purchasing file. No commitment, no commitment dance.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-emerald-500 text-white px-7 py-4 rounded-full font-semibold hover:bg-emerald-600 transition-all hover:gap-3 shadow-lg shadow-emerald-500/30"
          >
            Get started - free
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
